/**
 * Time model for basemag and survey-line data (phase 2b.1 P4; MB decisions 19 and 20; spec
 * docs/specs/basemag-import.md §1). Teslyn stores UTC instants (epoch milliseconds). A source with a UTC clock is
 * shown in the project offset; a source with a simple (local) clock is read in the project offset:
 * UTC = local − offset. Australia: UTC+8 fixed, no daylight saving. Rust mirror: crates/teslyn-core/src/basemag/time.rs.
 */

/** How a source's clock relates to UTC. */
export type ClockKind = 'utc' | 'local';

/** Australian projects: UTC+8 all year (decision 19). */
export const DEFAULT_PROJECT_OFFSET_H = 8;
export const MIN_OFFSET_H = -12;
export const MAX_OFFSET_H = 14;
/** Offsets are whole quarter hours (+5:45, +9:30 and so on). */
export const OFFSET_STEP_H = 0.25;

const MS_PER_H = 3_600_000;
const MS_PER_DAY = 86_400_000;

/** Whether `h` is a usable project offset: finite, −12 to +14, a multiple of 0.25 h. 0 is valid (decision 20). */
export function isValidOffset(h: number): boolean {
  return (
    Number.isFinite(h) &&
    h >= MIN_OFFSET_H &&
    h <= MAX_OFFSET_H &&
    Math.abs(h / OFFSET_STEP_H - Math.round(h / OFFSET_STEP_H)) < 1e-9
  );
}

const TIME = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** Minutes after midnight of an `HH:MM` time (a daily entry's take-off time), or null when invalid. */
export function parseTime(text: string | null): number | null {
  const m = text === null ? null : TIME.exec(text);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

/**
 * Read an offset typed by a person: `8`, `+8`, `-5`, `9.5`, `+09:30`, `UTC+10`, `0`. Returns null for anything else.
 * Never substitutes a default (the Diurnal tool's `parseFloat(v) || 8` turned 0 into 8, B-2).
 */
export function parseOffset(text: string): number | null {
  const t = text.trim().replace(/^utc\s*/i, '');
  if (t === '') return null;
  const hm = /^([+-]?)(\d{1,2}):(\d{2})$/.exec(t);
  let h: number;
  if (hm) {
    const minutes = Number(hm[3]);
    if (minutes >= 60) return null;
    h = (Number(hm[2]) + minutes / 60) * (hm[1] === '-' ? -1 : 1);
  } else if (/^[+-]?\d+(\.\d+)?$/.test(t)) {
    h = Number(t);
  } else {
    return null;
  }
  if (Object.is(h, -0)) h = 0;
  return isValidOffset(h) ? h : null;
}

/** `+8`, `+9:30`, `0`, `−5` (UTC prefix for labels: `UTC${formatOffset(h)}`). */
export function formatOffset(h: number): string {
  if (h === 0) return '+0';
  const sign = h < 0 ? '−' : '+';
  const a = Math.abs(h);
  const whole = Math.floor(a);
  const minutes = Math.round((a - whole) * 60);
  return minutes === 0 ? `${sign}${whole}` : `${sign}${whole}:${String(minutes).padStart(2, '0')}`;
}

/** Australia's zones by hours from UTC: the name people say. Teslyn holds an offset, not a place. */
const AUSTRALIAN_ZONES: Readonly<Record<string, string>> = {
  '8': 'AWST',
  '9.5': 'ACST',
  '10': 'AEST',
  '10.5': 'ACDT',
  '11': 'AEDT',
};

/** The name of a project's time zone for a person: "AWST", "ACST", and for any other offset "UTC+5:45". */
export function zoneName(offsetH: number): string {
  return AUSTRALIAN_ZONES[String(offsetH)] ?? `UTC${formatOffset(offsetH)}`;
}

const MONTH_WORDS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * An instant for a person to read (MB's R237, 5 Nov 2026): the project's local time first, UTC beside it, and the day:
 * "10:14 AWST (02:14 UTC) 5 Nov". When the two clocks are on different days each names its own, so an early start
 * never reads a day out: "06:30 AWST 5 Nov (22:30 UTC 4 Nov)". On a UTC project, one clock: "10:14 UTC 5 Nov".
 */
export function localAndUtcWords(ms: number, offsetH: number): string {
  const clock = (iso: string): string => iso.slice(11, 16);
  const day = (iso: string): string =>
    `${Number(iso.slice(8, 10))} ${MONTH_WORDS[Number(iso.slice(5, 7)) - 1]}`;
  const utc = new Date(ms).toISOString();
  const local = new Date(utcToLocalMs(ms, offsetH)).toISOString();
  if (offsetH === 0) return `${clock(utc)} UTC ${day(utc)}`;
  const zone = zoneName(offsetH);
  return day(local) === day(utc)
    ? `${clock(local)} ${zone} (${clock(utc)} UTC) ${day(local)}`
    : `${clock(local)} ${zone} ${day(local)} (${clock(utc)} UTC ${day(utc)})`;
}

/** The UTC instant of a local wall-clock reading in the project offset. */
export function localToUtcMs(localMs: number, offsetH: number): number {
  return localMs - Math.round(offsetH * MS_PER_H);
}

/** The local wall-clock time (as epoch-style ms) of a UTC instant in the project offset. */
export function utcToLocalMs(utcMs: number, offsetH: number): number {
  return utcMs + Math.round(offsetH * MS_PER_H);
}

/** Epoch ms of `jday` (1 = 1 January) of `year` at `secondsOfDay`, on the same calendar as the clock read. */
export function jdayToMs(year: number, jday: number, secondsOfDay: number): number {
  return Date.UTC(year, 0, 1) + (jday - 1) * MS_PER_DAY + Math.round(secondsOfDay * 1000);
}

export interface LocalParts {
  readonly year: number;
  /** Day of the year, 1 = 1 January. */
  readonly jday: number;
  readonly secondsOfDay: number;
  /** `HH:MM:SS`. */
  readonly hms: string;
}

/** Year, day of year and time of day of a UTC instant in the project offset (the Thomson "local day"). */
export function localParts(utcMs: number, offsetH: number): LocalParts {
  const local = utcToLocalMs(utcMs, offsetH);
  const d = new Date(local);
  const year = d.getUTCFullYear();
  const jday = Math.floor((local - Date.UTC(year, 0, 1)) / MS_PER_DAY) + 1;
  const secondsOfDay = (local - Date.UTC(year, d.getUTCMonth(), d.getUTCDate())) / 1000;
  const s = Math.floor(secondsOfDay);
  const hms = [Math.floor(s / 3600), Math.floor((s % 3600) / 60), s % 60]
    .map((x) => String(x).padStart(2, '0'))
    .join(':');
  return { year, jday, secondsOfDay, hms };
}

/** The UTC instant of a reading from a source with the given clock (`clockMs` is what the file says). */
export function readingUtcMs(clockMs: number, clock: ClockKind, offsetH: number): number {
  return clock === 'utc' ? clockMs : localToUtcMs(clockMs, offsetH);
}

/**
 * Year for a day number in a file that carries none (857 `.raw`, `_Diurnal.XYZ`): the previous day number's year,
 * moved on by one when the day number falls back by more than 180 days (a recording over New Year).
 */
export function nextYear(prevYear: number, prevJday: number | null, jday: number): number {
  return prevJday !== null && jday < prevJday - 180 ? prevYear + 1 : prevYear;
}
